import { Fragment, type PropsWithChildren } from 'react'
import { useToggle } from 'usehooks-ts'

import { DelimitedContent, FvButton, Icon, Slash } from '@fv/client-components'
import { locationAccessorialNormalizer } from '@fv/client-core'
import type { ShipmentLocation } from '@fv/client-types'

import { useTypeNames } from '@/hooks/useTypeNames'

import { CarrierTooltip } from '../CarrierTooltip'
import { StopScheduleTime } from './StopScheduleTime'

type StopAccessorialsProps = {
  location: ShipmentLocation
  loadId: string
  pickupDate?: Date | string
  allowEditTimes?: boolean
}

export const StopAccessorials = ({
  location,
  loadId,
  pickupDate,
  allowEditTimes,
}: StopAccessorialsProps) => {
  const accessorials = location?.accessorials ?? []
  const typeNames = useTypeNames()
  const normalizer = locationAccessorialNormalizer(typeNames)

  return (
    <span>
      <DelimitedContent delimiter={<Slash />}>
        {accessorials?.map(a => (
          <Fragment key={a.key}>
            {a.key === 'schedule' ? (
              <ScheduleAccessorial
                accessorial={a}
                appointmentTime={normalizer(a).appointmentTime}
                loadId={loadId}
                location={location}
                pickupDate={pickupDate}
                allowEdit={allowEditTimes}
              />
            ) : (
              <span>{normalizer(a).name}</span>
            )}
          </Fragment>
        ))}
      </DelimitedContent>
    </span>
  )
}

type ScheduleProps = {
  allowEdit?: boolean
  loadId: string
  accessorial: ShipmentLocation['accessorials'][number]
  appointmentTime?: string
  location: ShipmentLocation
  pickupDate?: Date | string
}
const ScheduleAccessorial = ({
  accessorial,
  appointmentTime,
  loadId,
  location,
  pickupDate,
  allowEdit,
}: ScheduleProps) => {
  const [showForm, toggleForm] = useToggle(false)

  const apptLabel = appointmentTime
    ? `Appt. ${appointmentTime}`
    : 'Appt. required'

  if (!allowEdit) {
    return (
      <ApptReqWrapper>
        <span>
          <Icon icon="clock" className="text-fv-gray-dark" />
          <span>{apptLabel}</span>
        </span>
      </ApptReqWrapper>
    )
  }

  return (
    <>
      <ApptReqWrapper>
        <CarrierTooltip
          label={
            appointmentTime
              ? 'Click to edit appointment'
              : 'Click to enter appointment'
          }
        >
          <FvButton icon="clock" onClick={toggleForm} theme="underlined">
            {appointmentTime ? apptLabel : 'Schedule required appointment'}
          </FvButton>
        </CarrierTooltip>
      </ApptReqWrapper>
      {showForm && (
        <StopScheduleTime
          accessorial={accessorial}
          loadId={loadId}
          location={location}
          pickupDate={pickupDate}
          onClose={toggleForm}
          className="border-y border-dashed border-fv-gray my-3 py-3"
        />
      )}
    </>
  )
}

const ApptReqWrapper = ({ children }: PropsWithChildren) => {
  return (
    <div className="inline-flex items-center gap-x-1">
      <span className="text-fv-orange">** </span>
      {children}
      <span className="text-fv-orange">** </span>
    </div>
  )
}
